import * as React from "react";
import { api, setExpiredHandler, type TariffStatus } from "../api";

interface TariffContextValue {
  status: TariffStatus | null;
  loading: boolean;
  reload: () => Promise<void>;
  has: (feature: string) => boolean;
}

const TariffContext = React.createContext<TariffContextValue | null>(null);

export function TariffProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<TariffStatus | null>(null);
  const [loading, setLoading] = React.useState(true);

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const t = await api.tariff();
      setStatus(t);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    reload();
  }, [reload]);

  // Har qanday yozuv 402 `expired` bilan rad etilsa — tarif holatini darhol
  // qayta o'qiymiz, shunda banner (App.tsx) keyingi mount'ni kutmaydi. Sessiya
  // uzilmaydi: egasi Sozlamalarda aktivatsiya kodini kiritib davom etadi.
  React.useEffect(() => {
    setExpiredHandler(() => {
      void reload();
    });
    return () => setExpiredHandler(null);
  }, [reload]);

  const has = React.useCallback(
    (feature: string) =>
      !!status && (status.features ?? []).includes(feature),
    [status],
  );

  return (
    <TariffContext.Provider value={{ status, loading, reload, has }}>
      {children}
    </TariffContext.Provider>
  );
}

export function useTariff() {
  const ctx = React.useContext(TariffContext);
  if (!ctx) throw new Error("useTariff must be used inside TariffProvider");
  return ctx;
}

export function useFeature(feature: string): boolean {
  const { has } = useTariff();
  return has(feature);
}
